import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import {
  Box,
  Button,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import usePayrollExport from "../../hooks/usePayrollExport";
import usePage from "../../hooks/usePage";

const EXPORT_LAYOUTS = [
  {
    value: "preapproval_transactions",
    label: "Pre-Approval - Pay Transactions",
    description: "Pay transactions before management approval",
  },
  {
    value: "adp_hours_mileage",
    label: "ADP Layout - Hours & Mileage",
    description: "ADP payroll format for call, mileage, and home mileage",
  },
  {
    value: "adp_expenses",
    label: "ADP Layout - Expenses",
    description: "ADP payroll format for expense transactions only",
  },
  {
    value: "postapproval_transactions",
    label: "Post-Approval - Pay Transactions",
    description: "Pay transactions after management approval",
  },
  {
    value: "epioqa4_syndicated",
    label: "EPIOQA4 - Syndicated",
    description: "Syndication reconciliation export",
  },
];

const LEGACY_LAYOUT_LABELS = {
  preapproval_adp: "ADP Layout - Hours & Mileage",
  postapproval_adp: "ADP Layout - Hours & Mileage",
  adp: "ADP Layout - Hours & Mileage",
};

const PayrollExports = ({ payPeriodId, payPeriod }) => {
  const { email } = useSelector((state) => state.authentication);
  const { showMessage } = usePage();
  const { exports, loading, requestExport, getExportHistory, retryExport } =
    usePayrollExport();

  const [layout, setLayout] = useState("");
  const [exportEmail, setExportEmail] = useState(email || "");
  const [filters, setFilters] = useState({
    userIds: [],
    projectIds: [],
    transactionStatus: null,
  });
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedExport, setSelectedExport] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load export history on component mount
  useEffect(() => {
    if (payPeriodId) {
      loadExportHistory();
    }
  }, [payPeriodId]);

  const loadExportHistory = async () => {
    await getExportHistory(payPeriodId);
  };

  const handleRequestExport = () => {
    setShowRequestDialog(true);
  };

  const handleSubmitExportRequest = async () => {
    if (!layout) {
      showMessage("Please select an export layout", "error");
      return;
    }

    if (!exportEmail) {
      showMessage("Please enter an email address", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      await requestExport(layout, payPeriodId, exportEmail, filters);
      setShowRequestDialog(false);
      setLayout("");
      setFilters({
        userIds: [],
        projectIds: [],
        transactionStatus: null,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetryExport = async (exportId) => {
    setIsSubmitting(true);
    try {
      await retryExport(exportId);
      await loadExportHistory();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDetails = (exportRecord) => {
    setSelectedExport(exportRecord);
    setDetailsDialogOpen(true);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { label: "Pending", color: "info" },
      processing: { label: "Processing", color: "warning" },
      completed: { label: "Completed", color: "success" },
      error: { label: "Error", color: "error" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        label={config.label}
        color={config.color}
        variant="outlined"
      />
    );
  };

  const getLayoutLabel = (layoutValue) => {
    if (LEGACY_LAYOUT_LABELS[layoutValue]) {
      return LEGACY_LAYOUT_LABELS[layoutValue];
    }

    const layout = EXPORT_LAYOUTS.find((l) => l.value === layoutValue);
    return layout ? layout.label : layoutValue;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return format(new Date(date), "MMM dd, yyyy HH:mm");
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Box>
      {/* Request Export Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          onClick={handleRequestExport}
          disabled={!payPeriodId || loading || isSubmitting}
        >
          Generate Export
        </Button>
      </Box>

      {/* Export History Table */}
      <Box>
        <Typography
          variant="h6"
          sx={{ mb: 2 }}
        >
          Export History
        </Typography>

        {loading && exports.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : exports.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Layout</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Records</TableCell>
                  <TableCell align="right">File Size</TableCell>
                  <TableCell>Generated</TableCell>
                  <TableCell>Downloads</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exports.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell>{getLayoutLabel(exp.layout)}</TableCell>
                    <TableCell>{getStatusChip(exp.status)}</TableCell>
                    <TableCell sx={{ fontSize: "0.9em" }}>
                      {exp.email}
                    </TableCell>
                    <TableCell align="right">
                      {exp.recordCount || "-"}
                    </TableCell>
                    <TableCell align="right">
                      {formatFileSize(exp.fileSize)}
                    </TableCell>
                    <TableCell>{formatDate(exp.generatedAt)}</TableCell>
                    <TableCell align="center">
                      {exp.downloadCount || 0}
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1}
                      >
                        {exp.status === "error" && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleRetryExport(exp.id)}
                            disabled={isSubmitting}
                          >
                            Retry
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewDetails(exp)}
                        >
                          Details
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            variant="body2"
            sx={{ p: 2, fontStyle: "italic" }}
          >
            No exports yet for this pay period
          </Typography>
        )}
      </Box>

      {/* Request Export Dialog */}
      <Dialog
        open={showRequestDialog}
        onClose={() => !isSubmitting && setShowRequestDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Payroll Export</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            {/* Layout Selection */}
            <FormControl fullWidth>
              <InputLabel>Export Layout</InputLabel>
              <Select
                value={layout}
                label="Export Layout"
                onChange={(e) => setLayout(e.target.value)}
                disabled={isSubmitting}
              >
                {EXPORT_LAYOUTS.map((l) => (
                  <MenuItem
                    key={l.value}
                    value={l.value}
                  >
                    {l.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Layout Description */}
            {layout && (
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    {
                      EXPORT_LAYOUTS.find((l) => l.value === layout)
                        ?.description
                    }
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Pay Period Info */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2">
                  <strong>Pay Period:</strong> {payPeriod?.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {payPeriod?.status}
                </Typography>
              </CardContent>
            </Card>

            {/* Email Address */}
            <TextField
              label="Email Address"
              type="email"
              value={exportEmail}
              onChange={(e) => setExportEmail(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              helperText="Export will be sent to this email address"
            />

            {/* Optional: Filters */}
            <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1 }}
              >
                Filters (Optional)
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
              >
                Leave empty to export all records
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowRequestDialog(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitExportRequest}
            variant="contained"
            disabled={!layout || !exportEmail || isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Generate Export"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Details</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedExport && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2">
                  <strong>Layout:</strong>{" "}
                  {getLayoutLabel(selectedExport.layout)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">
                  <strong>Status:</strong>{" "}
                  {getStatusChip(selectedExport.status)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedExport.email}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">
                  <strong>Records:</strong> {selectedExport.recordCount || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">
                  <strong>File Size:</strong>{" "}
                  {formatFileSize(selectedExport.fileSize)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">
                  <strong>Generated:</strong>{" "}
                  {formatDate(selectedExport.generatedAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">
                  <strong>Downloads:</strong>{" "}
                  {selectedExport.downloadCount || 0}
                </Typography>
              </Box>
              {selectedExport.errorMessage && (
                <Box sx={{ p: 2, backgroundColor: "#ffebee", borderRadius: 1 }}>
                  <Typography
                    variant="body2"
                    color="error"
                  >
                    <strong>Error:</strong> {selectedExport.errorMessage}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollExports;
